import { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { SUPPORT_EMAIL, SUPPORT_PHONE_DISPLAY, SUPPORT_PHONE_TEL, SUPPORT_HOURS } from '@/lib/support';
import { api } from '@/lib/api';

const UPDATED = 'July 2026';

function LegalShell({ kicker, title, children }: { kicker: string; title: string; children: React.ReactNode }) {
  return (
    <div className="bg-cream min-h-[70vh]">
      <section className="section-dark relative text-cream py-16 overflow-hidden" style={{ background: 'linear-gradient(135deg,#0F0F12,#1a1208 55%,#0F0F12)' }}>
        <div className="absolute inset-0 dotgrid-light bg-[length:18px_18px] opacity-15" />
        <div className="relative max-w-[820px] mx-auto px-6">
          <div className="stamp text-gold text-[10px]">{kicker}</div>
          <h1 className="display text-4xl md:text-5xl mt-2">{title}</h1>
          <p className="text-cream/55 text-sm mt-3">Last updated {UPDATED}</p>
        </div>
      </section>
      <div className="max-w-[820px] mx-auto px-6 py-12 legal-prose">{children}</div>
    </div>
  );
}

function Section({ n, title, children }: { n: string; title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="display text-xl text-ink mb-2">{n}. {title}</h2>
      <div className="text-ink-700 text-[15px] leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

/* ─────────────────────────── Terms ─────────────────────────── */
export function Terms() {
  return (
    <LegalShell kicker="LEGAL" title="Terms of Service">
      <p className="text-ink-700 text-[15px] leading-relaxed mb-8">
        These terms govern your use of Souqli, a recommerce marketplace for pre-loved fashion, tech and lifestyle goods.
        By creating an account or buying and selling on Souqli, you agree to them.
      </p>
      <Section n="1" title="Your account">
        <p>You must be 18 or over to sell, and provide accurate details. You are responsible for activity under your account and for keeping your password secure. Sellers complete an identity and bank check (via Stripe) before listing or receiving payouts.</p>
      </Section>
      <Section n="2" title="Buying">
        <p>When you buy an item, your payment is held securely until you confirm receipt. Funds are released to the seller only after you confirm you have collected the item and it matches its listing, or after the automatic release window passes. A 4.5% + 70p buyer protection fee (members pay nothing on up to 12 orders and £300 of spend each month, then 3.5% + 60p) and delivery are shown at checkout before you pay.</p>
      </Section>
      <Section n="3" title="Selling">
        <p>You must own the items you list and describe their condition honestly. Prohibited, counterfeit or stolen goods are not allowed and will be removed. Listings are reviewed before going live. You keep the full item price; Souqli's revenue comes from the buyer protection fee paid by the buyer.</p>
      </Section>
      <Section n="4" title="Delivery & collection">
        <p>Items ship through our partner carriers (locker, tracked or express, chosen at checkout). Tracking is provided. Buyers collect within the stated window; sellers must dispatch promptly once notified.</p>
      </Section>
      <Section n="5" title="Disputes & refunds">
        <p>If an item does not arrive or does not match its description, open a dispute within the window shown on your order. Funds remain held while Souqli mediates. Approved disputes are refunded to you. Full details are in our <Link to="/refund-policy" className="text-gold-dark hover:underline">Refund &amp; Buyer Protection Policy</Link>, and nothing here removes your statutory consumer rights under the Consumer Rights Act 2015 or the Consumer Contracts Regulations 2013.</p>
      </Section>
      <Section n="6" title="Membership">
        <p>Souqli Member is an optional paid subscription of £10.99 per month. Benefits (no buyer protection fee on up to 12 orders and £300 of spend each calendar month, then 3.5% + 60p, plus member perks) apply while your membership is active. You can cancel future renewal any time from your dashboard and keep benefits until the end of the paid period.</p>
      </Section>
      <Section n="7" title="Liability">
        <p>Souqli provides the marketplace and a buyer-protection service — payments are processed by Stripe and the seller's payout is held until you confirm receipt — but Souqli is not the manufacturer of listed items. To the extent permitted by law, our liability is limited to the value of the affected order. Nothing here removes your statutory consumer rights.</p>
      </Section>
      <Section n="8" title="Changes & contact">
        <p>We may update these terms; material changes will be notified in-app. Questions? Email <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gold-dark hover:underline">{SUPPORT_EMAIL}</a> or visit our <Link to="/contact" className="text-gold-dark hover:underline">contact page</Link>.</p>
      </Section>
    </LegalShell>
  );
}

/* ─────────────────────────── Cookies ─────────────────────────── */
export function Cookies() {
  const rows = [
    ['Essential', 'Sign-in, session and security (e.g. keeping you logged in, secure checkout).', 'Always on'],
    ['Preferences', 'Remembering choices like your saved items, filters and delivery method.', 'Optional'],
    ['Analytics', 'Aggregated, anonymised usage to improve the product. No cross-site ad tracking.', 'Optional'],
  ];
  return (
    <LegalShell kicker="LEGAL" title="Cookie Policy">
      <p className="text-ink-700 text-[15px] leading-relaxed mb-8">
        Cookies are small files stored on your device. Souqli uses them to keep you signed in, remember your preferences, and understand how the marketplace is used. We do not use cookies for cross-site advertising.
      </p>
      <div className="rounded-luxe border border-warmline overflow-x-auto mb-8">
        <div className="min-w-[480px]">
          <div className="section-dark grid grid-cols-[120px_1fr_100px] bg-ink text-cream/80 text-[11px] uppercase tracking-widest px-4 py-2.5">
            <span>Type</span><span>Purpose</span><span className="text-right">Control</span>
          </div>
          {rows.map(([t, p, c], i) => (
            <div key={t} className={`grid grid-cols-[120px_1fr_100px] px-4 py-3 text-[13px] border-t border-warmline ${i % 2 ? 'bg-cream' : 'bg-white'}`}>
              <span className="font-medium text-ink">{t}</span>
              <span className="text-mute">{p}</span>
              <span className="text-right text-gold-dark">{c}</span>
            </div>
          ))}
        </div>
      </div>
      <Section n="1" title="Managing cookies">
        <p>You can clear or block cookies in your browser settings. Blocking essential cookies may stop sign-in and checkout from working. Optional cookies can be declined without affecting core functionality.</p>
      </Section>
      <Section n="2" title="Questions">
        <p>Email <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gold-dark hover:underline">{SUPPORT_EMAIL}</a> for anything cookie-related.</p>
      </Section>
    </LegalShell>
  );
}

/* ─────────────────────────── Contact ─────────────────────────── */
export function Contact() {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', subject: 'General enquiry', message: '' });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error('Please fill in your name, email and message.'); return; }
    setSending(true);
    try {
      await api.post('/support/contact', form);
      setSent(true);
      toast.success('Message sent - we\'ll be in touch.');
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? `Could not send just now - please email ${SUPPORT_EMAIL} directly.`);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="bg-cream min-h-[70vh]">
      <section className="section-dark relative text-cream py-16 overflow-hidden" style={{ background: 'linear-gradient(135deg,#0F0F12,#1a1208 55%,#0F0F12)' }}>
        <div className="absolute inset-0 dotgrid-light bg-[length:18px_18px] opacity-15" />
        <div className="relative max-w-[900px] mx-auto px-6">
          <div className="stamp text-gold text-[10px]">SUPPORT</div>
          <h1 className="display text-4xl md:text-5xl mt-2">Get in touch</h1>
          <p className="text-cream/60 text-sm mt-3 max-w-[52ch]">Questions about an order, a listing, your account or membership? We usually reply within one business day.</p>
        </div>
      </section>

      <div className="max-w-[900px] mx-auto px-6 py-12 grid md:grid-cols-[1fr_300px] gap-8 items-start">
        <form onSubmit={submit} className="bg-white border border-warmline rounded-luxe shadow-luxe p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Your name</label>
              <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" />
            </div>
            <div>
              <label className="label">Email</label>
              <input className="input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" />
            </div>
          </div>
          <div>
            <label className="label">Topic</label>
            <select className="input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}>
              {['General enquiry', 'Order or delivery', 'Selling & payouts', 'Membership', 'Report a problem', 'Press'].map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Message</label>
            <textarea className="input min-h-[140px]" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="How can we help?" />
          </div>
          <button type="submit" disabled={sending || sent} className="btn btn-primary w-full">{sent ? 'Sent - thank you' : sending ? 'Sending…' : 'Send message →'}</button>
          <p className="text-[11px] text-mute text-center">We usually reply within one business day.</p>
        </form>

        <aside className="space-y-4">
          <div className="bg-white border border-warmline rounded-luxe p-5">
            <div className="stamp text-gold-dark text-[9px] mb-2">CALL US</div>
            <a href={`tel:${SUPPORT_PHONE_TEL}`} className="text-sm text-ink font-medium hover:text-gold-dark">{SUPPORT_PHONE_DISPLAY}</a>
            <p className="text-[12px] text-mute mt-2">{SUPPORT_HOURS}</p>
          </div>
          <div className="bg-white border border-warmline rounded-luxe p-5">
            <div className="stamp text-gold-dark text-[9px] mb-2">EMAIL US</div>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-sm text-ink hover:text-gold-dark break-all">{SUPPORT_EMAIL}</a>
            <p className="text-[12px] text-mute mt-2">Support, seller help, press and partnerships.</p>
          </div>
          <div className="bg-white border border-warmline rounded-luxe p-5">
            <div className="stamp text-gold-dark text-[9px] mb-2">REGISTERED COMPANY</div>
            <p className="text-sm text-ink font-medium">Souqli UK Ltd</p>
            <p className="text-[12px] text-mute mt-1">Registered in England &amp; Wales</p>
            <p className="text-[12px] text-mute">Company No. 17471689</p>
            <p className="text-[12px] text-mute">66 Paul Street, London, England, EC2A 4NA</p>
          </div>
          <div className="bg-white border border-warmline rounded-luxe p-5">
            <div className="stamp text-gold-dark text-[9px] mb-2">HELP YOURSELF</div>
            <ul className="space-y-2 text-[13px]">
              <li><Link to="/help" className="text-ink hover:text-gold-dark">Browse the Help Centre →</Link></li>
              <li><Link to="/trust" className="text-ink hover:text-gold-dark">How Souqli works &amp; buyer protection →</Link></li>
              <li><Link to="/membership" className="text-ink hover:text-gold-dark">Membership &amp; benefits →</Link></li>
              <li><Link to="/dashboard/sell" className="text-ink hover:text-gold-dark">Start selling →</Link></li>
            </ul>
          </div>
          <div className="rounded-luxe p-5 text-cream" style={{ background: 'linear-gradient(135deg,#D98A2B,#A66018)' }}>
            <div className="display text-lg text-ink">Response time</div>
            <p className="text-ink/70 text-[13px] mt-1">Most messages answered within 1 business day. Order issues are prioritised.</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
